import React, { useEffect, useState } from 'react';
import useCurrentUser from '../hooks/useCurrentUser';
import { EUserType } from '../data_models/enums';
import FreelancerMyProjects from '../components/project/FreelancerMyProjects';
import EmployerMyProjects from '../components/project/EmployerMyProjects';
import { OutlinedButton } from '../components/common/Button';
import { EProjectStatus, IProject } from '../data_models/project';
import useRouting from '../hooks/useRouting';
import useMyProjects from '../hooks/useMyProjects';
import { DEFAULT_USER_PROFILE } from '../config/defaultAssetUrls';
import Card from '../components/common/Card';
import SmallAvatar from '../components/common/SmallAvatar';
import { ProjectStatusBatch } from '../components/common/StatusBatch';
import { dateToShortReadable } from '../utils/dateUtils';
import ProgressBar from '../components/common/ProgressBar';
import NamePlateFreelancer from '../components/common/NamePlateFreelancer';
import SmallAvatarGroup from '../components/common/SmallAvatarGroup';
import { FlatButton } from '../components/common/Button';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import { ConfirmDeleteProjectDraftLightbox } from '../components/lightboxes/projects/ConfirmDeleteProjectDraftLightbox';
import RichText from '../components/common/RichText';
import { IFreelancer } from '../data_models/freelancer';
import useUser from '../hooks/useUser';
import { IoShieldCheckmark, IoTimeOutline } from 'react-icons/io5';
import { IoShieldOutline } from 'react-icons/io5';
import { IoShieldCheckmarkOutline } from 'react-icons/io5';
import Tooltip from '../components/common/Tooltip';



const MyProjectsPage: React.FC = () => {


  const { currentUser, currentUserType } = useCurrentUser();

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Blurred dots */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-primary-500/30 blur-3xl -z-10" />
      <div className="absolute -top-12 left-64 w-64 h-64 rounded-full bg-primary-700/25 blur-3xl -z-10" />
      <div className="absolute -bottom-10 -right-5 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl -z-10" />
      <div className="absolute bottom-36 right-5 w-64 h-64 rounded-full bg-secondary-500/20 blur-3xl -z-10" />
      <div className="absolute bottom-12 left-12 w-48 h-48 rounded-full bg-primary-200/30 blur-3xl -z-10" />

      <div className='w-full flex p-4 space-x-4'>
        <>
          {currentUserType === EUserType.FREELANCER
            ? <div className='w-full '><FreelancerMyProjects /></div>
            : currentUserType === EUserType.EMPLOYER
              ? <div className='w-full '>
                <EmployerMyProjects />
              </div>
              : <div className='flex flex-col items-center w-full my-10'>
                <h1>You need to be logged in to access this page</h1>
              </div>
          }

        </>
      </div>
    </div>

  );
};


interface ProjectCardProps {
  project: IProject;
  className?: string;
  onClick?: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, className, onClick }) => {

  const { fetchBriefFreelancersByIds } = useUser();
  const { currentUserType } = useCurrentUser();

  const [team, setTeam] = useState<IFreelancer[]>([]);

  useEffect(() => {
    if (project.assignedFreelancers) {
      const freelancerIds: Set<string> = new Set();
      project.currentProjectManager && freelancerIds.add(project.currentProjectManager);
      project.currentRoadmapVerificationPm && freelancerIds.add(project.currentRoadmapVerificationPm);
      for (const freelancer of project.assignedFreelancers) {
        freelancerIds.add(freelancer.freelancerId);
      }

      if (freelancerIds.size > 0) {
        fetchBriefFreelancersByIds(Array.from(freelancerIds)).then(freelancers => {
          setTeam(freelancers);
        });
      }
    }
  }, [project]);

  return (
    <div className={`p-3 bg-white border border-gray-200 rounded-lg shadow-sm ${className}`} onClick={onClick}>
      {/* Header Section */}
      <div className='flex flex-col gap-2'>
        <div className='flex flex-row items-start gap-2'>
          <span className='text-base font-bold line-clamp-1'>{project.title}</span>
          <ProjectStatusBatch status={project.status} variant="flat" />
        </div>


        {/* Roadmap verification status && Total effort*/}
        <div className='grid grid-cols-2 gap-2 text-sm'>
          <div className='flex flex-row items-center gap-2 text-gray-500'>
            <span className='text-sm'>Created</span>
            <span className='text-sm font-semibold'>{dateToShortReadable(project._createdAt)}</span>
          </div>
          {(project.status || 0) >= EProjectStatus.COMPLETED ? <div className='flex flex-row items-center gap-2'>
            <span className='text-sm'>Completed</span>
            <span className='text-sm font-semibold'>{dateToShortReadable(project._completedAt)}</span>
          </div> : <div />}

          {/* Roadmap verification status */}
          {!project._roadmapVerifiedAt && project.currentRoadmapVerificationPm ? (
            <div className='flex items-center text-green-500'>
              <IoShieldCheckmarkOutline className='mr-1.5 size-5 shrink-0' />
              <span>Roadmap Review in Progress</span>
            </div>
          ) : project._waitingForRoadmapVerification && !project.currentRoadmapVerificationPm ? (
            <div className='flex items-center text-lime-500'>
              <IoShieldOutline className='mr-1.5 size-5 shrink-0 ' />
              <span>Expert Assignment in Progress</span>
            </div>
          ) : !project._waitingForRoadmapVerification && project._roadmapVerifiedAt ? (
            <div className='flex items-center text-secondary-500'>
              <IoShieldCheckmark className='mr-1.5 size-5 shrink-0' />
              <span>Roadmap Verified</span>
            </div>
          ) : (
            <div className='flex items-center text-gray-500'>
              <IoShieldOutline className='mr-1.5 size-5 shrink-0' />
              <span>Roadmap Not Verified</span>
            </div>
          )}

          {/* Effort */}

          <div className="flex items-center text-sm text-gray-500">
            <IoTimeOutline className='mr-1.5 size-5 shrink-0 text-gray-400' />
            {project.totalCredits} {currentUserType === EUserType.EMPLOYER ? 'Hrs' : 'Credits'}
          </div>

        </div>
      </div>

      <hr className='my-2' />

      {/* Body section */}
      <div>
        <span className='text-sm'>{project.completedTasks?.length} / {project.tasks?.length} tasks completed</span>
        <div className="flex flex-row items-center space-x-4">
          <div className="flex-1">
            <ProgressBar
              highestValue={100}
              lowestValue={0}
              actualValue={project.percentageCompleted || 0}
            />
          </div>
          <p className="text-sm">{Number(project.percentageCompleted).toFixed(0) || 0}%</p>
        </div>
      </div>

      {/* Footer section */}
      <div>
        <span className='text-sm'>{team.length} Team Members</span>
        <SmallAvatarGroup imageUrls={team.map(freelancer => freelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE)} showNumberAvatars={3} />
      </div>
    </div>
  );
};



export default MyProjectsPage;